/**
 * 钢笔工具（M3-15 / DESIGN.md §6.3，P 键；补充：工具模式=形状/路径）。
 *
 * 点击落角点（直线段）；按住拖拽落平滑点——拖拽终点即出控制柄，
 * 入控制柄按锚点镜像（PS 的标准手感：对称控制柄，曲线在锚点处 C¹ 连续）。
 * 点回起点闭合、Enter 提交开放路径（无收尾曲线段）；Esc 放弃整段会话，
 * 不留任何历史。
 *
 * 工具模式（属性栏「模式」选项，跟真实 Photoshop 一致）：
 * - `shape`：提交即建一个真正上色的形状图层（旧行为，默认）
 * - `path`：提交只落一条不上色的「工作路径」（`workPath`），不进文档、
 *   不产生历史——之后靠 `convertToSelection`/`convertToShape`/`convertToSavedPath`
 *   显式转换（对应真实 PS 的 Ctrl+Enter「建立选区」、点属性栏「形状」图标、
 *   Paths 面板"存储工作路径"）
 */
import {
  mirrorHandle,
  pathToCommands,
  type Anchor,
  type Point,
} from '../../engine/path/anchor';
import { pathToSelection } from '../../engine/path/rasterize';
import { createAddLayerCommand } from '../../app/commands/layer-commands';
import { createShapeLayer } from '../../app/commands/shape-commands';
import { deleteLayer } from '../../app/layer-edit';
import { savePath } from '../../app/paths';
import { layerId, type LayerId } from '../../engine/tile/key';
import type { AppStore } from '../../app/store';
import type { VectorFill, VectorStroke } from '../../document/types';
import type { ToolManager } from './registry';

export interface PenDeps {
  readonly store: AppStore;
  readonly tools: ToolManager;
  readonly requestRender: () => void;
  /** 模式=shape 时落盘用的填充/描边——跟形状工具共用同一份属性栏取色状态。 */
  readonly fill: () => VectorFill;
  readonly stroke: () => VectorStroke | undefined;
}

interface WorkPath {
  readonly anchors: readonly Anchor[];
  readonly closed: boolean;
}

/** 点回起点视为闭合的容差（文档像素）。 */
const CLOSE_TOLERANCE = 6;

export class PenToolController {
  readonly #deps: PenDeps;
  #anchors: Anchor[] = [];
  /** 当前拖拽中的锚点下标；-1 = 未在拖拽。 */
  #dragIndex = -1;
  #layerSeq = 0;
  #pathSeq = 0;
  /** 模式=path 时提交出的工作路径——不进文档，只是等着被转换。 */
  #workPath: WorkPath | null = null;
  /**
   * 模式=shape 时刚刚落盘那个形状图层——记住它的锚点，Cmd+Enter 也能
   * 从"已经闭合、已经是真图层"这一步倒回去转成选区（真实使用中，用户
   * 点回起点闭合的那一下就已经提交成形状了，"还在画的锚点"早就清空，
   * 这时候按 Cmd+Enter 不该什么都不做）。只要这个图层还是活动图层、
   * 没被别的操作顶替，就认为它还是"刚画的那条线"。
   */
  #lastCommittedShape: { readonly layerId: LayerId; readonly anchors: readonly Anchor[] } | null = null;

  constructor(deps: PenDeps) {
    this.#deps = deps;
  }

  /** 当前是否有一条等待转换的工作路径（模式=path 提交后）。 */
  get workPath(): WorkPath | null {
    return this.#workPath;
  }

  get active(): boolean {
    return this.#anchors.length > 0;
  }

  /** 只读预览：已放置的锚点（供叠加层画路径）。 */
  get anchors(): readonly Anchor[] {
    return this.#anchors;
  }

  handlePointer(
    phase: 'down' | 'move' | 'up' | 'double',
    point: Point,
  ): boolean {
    if (this.#deps.tools.active.id !== 'pen') return false;
    if (this.#deps.store.document === null) return true;

    if (phase === 'down') {
      const first = this.#anchors[0];
      if (first !== undefined && this.#anchors.length >= 2) {
        const dist = Math.hypot(point.x - first.point.x, point.y - first.point.y);
        if (dist <= CLOSE_TOLERANCE) {
          this.#commit(true);
          return true;
        }
      }
      this.#anchors.push({ point });
      this.#dragIndex = this.#anchors.length - 1;
      this.#deps.requestRender();
      return true;
    }
    if (phase === 'move' && this.#dragIndex >= 0) {
      const anchor = this.#anchors[this.#dragIndex]!;
      this.#anchors[this.#dragIndex] = {
        point: anchor.point,
        handleOut: point,
        handleIn: mirrorHandle(anchor.point, point),
      };
      this.#deps.requestRender();
      return true;
    }
    if (phase === 'up') {
      this.#dragIndex = -1;
      return true;
    }
    if (phase === 'double') {
      this.#commit(false);
      return true;
    }
    return true;
  }

  /** Enter：提交开放路径。 */
  commitOpen(): boolean {
    return this.#commit(false);
  }

  /** Esc：放弃会话，不产生历史（正在编辑的工作路径不受影响，那是已经提交过的独立状态）。 */
  cancel(): void {
    this.#anchors = [];
    this.#dragIndex = -1;
    this.#deps.requestRender();
  }

  #commit(closed: boolean): boolean {
    const anchors = this.#anchors;
    this.#anchors = [];
    this.#dragIndex = -1;
    if (anchors.length < 2) {
      this.#deps.requestRender();
      return false;
    }

    // 先把状态改完再 requestRender——它触发的是同步的信号更新（Solid 在没有
    // 显式 batch() 的情况下会立刻把订阅它的计算跑一遍），如果先调用
    // requestRender 再改 #workPath，订阅方读到的还是改之前的旧值，
    // 这一帧的重渲染就白读了（真实 bug：属性栏的"建立选区/建立形状"按钮
    // 因此一直读到 workPath===null，永远不出现）。
    let committed: boolean;
    if (this.#deps.tools.getOption('pen', 'mode') === 'path') {
      this.#workPath = { anchors, closed };
      committed = true;
    } else {
      committed = this.#commitShape(anchors, closed, this.#deps.fill(), this.#deps.stroke());
    }
    this.#deps.requestRender();
    return committed;
  }

  #commitShape(
    anchors: readonly Anchor[],
    closed: boolean,
    fill: VectorFill,
    stroke: VectorStroke | undefined,
  ): boolean {
    const store = this.#deps.store;
    const document = store.document;
    if (document === null) return false;

    const commands = pathToCommands({ anchors, closed, fillRule: 'nonzero' });
    const id = layerId(`shape-${++this.#layerSeq}-${Date.now().toString(36)}`);
    const layer = createShapeLayer(
      id,
      `形状 ${document.root.children.length + 1}`,
      { commands, fillRule: 'nonzero' },
      fill,
      stroke,
    );
    store.dispatch(
      createAddLayerCommand({ layer, parentId: null, index: document.root.children.length }),
    );
    store.setActiveLayer(id);
    this.#lastCommittedShape = { layerId: id, anchors };
    return true;
  }

  /**
   * 路径（不管闭合与否）→ 选区，转换后清空——真实 PS 建立选区之后，
   * 画面上那条路径的轮廓线也确实不再显示（路径本身还留在 Paths 面板里，
   * 只是不再是"当前显示"的那条；这个项目没有 Paths 面板，效果上就是清空）。
   * 选区永远按闭合处理：选区必须是闭合区域，哪怕调用方传来的是未闭合路径。
   */
  #convertAnchorsToSelectionAndHide(anchors: readonly Anchor[] | null): boolean {
    const document = this.#deps.store.document;
    if (anchors === null || anchors.length < 2 || document === null) return false;
    const commands = pathToCommands({ anchors, closed: true, fillRule: 'nonzero' });
    const mask = pathToSelection(document.size.width, document.size.height, commands, 'nonzero');
    this.#deps.store.setSelection(mask);
    this.#anchors = [];
    this.#dragIndex = -1;
    this.#workPath = null;
    this.#deps.requestRender();
    return true;
  }

  /** 工作路径 → 选区（属性栏「建立选区」按钮）。 */
  convertToSelection(): boolean {
    return this.#convertAnchorsToSelectionAndHide(this.#workPath?.anchors ?? null);
  }

  /**
   * Cmd+Return（Mac）/ Ctrl+Enter（Win）：不管当前是「形状」还是「路径」
   * 模式，也不管路径正在画（未闭合）还是已经提交成工作路径，一律转成
   * 选区并隐藏钢笔画的线——这把快捷键的语义是"我现在只要选区"，跟属性栏
   * 「建立选区」按钮（针对模式=path 提交后的工作路径）是同一份底层逻辑，
   * 只是多接了一路"还在画、没提交"的情形。没有任何路径可转换时返回
   * false，调用方据此决定要不要 preventDefault——不该在什么都没做的
   * 情况下抢占这个键位（真实 PS 也是这个键位，不是我们自造的）。
   */
  convertCurrentToSelectionAndHide(): boolean {
    if (this.#deps.tools.active.id !== 'pen') return false;
    if (this.#anchors.length >= 2 || this.#workPath !== null) {
      const anchors = this.#anchors.length >= 2 ? this.#anchors : this.#workPath?.anchors ?? null;
      return this.#convertAnchorsToSelectionAndHide(anchors);
    }
    // 没有正在画的路径、也没有工作路径——但模式=shape 时点回起点闭合的
    // 那一下已经把路径落盘成真正的形状图层了（见 #commitShape），"路径"
    // 早就不是路径了。只要那个图层还是当前活动图层（没被后续操作顶替），
    // 就把它删掉换成对应的选区，这样 Cmd+Enter 才是"无论路径还是形状"
    // 都生效，而不是只在"还没闭合"这一个时间窗口里生效。
    const lastShape = this.#lastCommittedShape;
    if (lastShape !== null && this.#deps.store.activeLayerId === lastShape.layerId) {
      deleteLayer(this.#deps.store, lastShape.layerId);
      this.#lastCommittedShape = null;
      return this.#convertAnchorsToSelectionAndHide(lastShape.anchors);
    }
    return false;
  }

  /** 工作路径 → 形状图层（真实 PS 属性栏点「形状」图标）。转换后清空工作路径。 */
  convertToShape(): boolean {
    const workPath = this.#workPath;
    if (workPath === null) return false;
    const committed = this.#commitShape(workPath.anchors, workPath.closed, this.#deps.fill(), this.#deps.stroke());
    if (committed) {
      this.#workPath = null;
      this.#deps.requestRender();
    }
    return committed;
  }

  /**
   * 工作路径 → 保存为 Paths 面板里一条命名路径（ADR-0011）。转换后清空
   * 工作路径——跟 `convertToSelection`/`convertToShape` 同一个"转换即
   * 清空"的约定，转换之后画面上不该还留着那条正在编辑的路径线。
   */
  convertToSavedPath(): boolean {
    const workPath = this.#workPath;
    if (workPath === null) return false;
    const commands = pathToCommands({ anchors: workPath.anchors, closed: workPath.closed, fillRule: 'nonzero' });
    const committed = savePath(this.#deps.store, { commands, fillRule: 'nonzero' }, () =>
      `path-${Date.now().toString(36)}-${++this.#pathSeq}`,
    );
    if (committed) {
      this.#workPath = null;
      this.#deps.requestRender();
    }
    return committed;
  }

  /** 画一组锚点+连线+控制柄，`closed` 时把终点接回起点。 */
  #drawAnchors(ctx: CanvasRenderingContext2D, anchors: readonly Anchor[], closed: boolean): void {
    if (anchors.length === 0) return;
    ctx.save();
    ctx.strokeStyle = 'rgba(255,255,255,0.9)';
    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    ctx.lineWidth = 1;

    ctx.beginPath();
    ctx.moveTo(anchors[0]!.point.x, anchors[0]!.point.y);
    const segments = closed ? [...anchors, anchors[0]!] : anchors;
    for (let i = 1; i < segments.length; i++) {
      const from = segments[i - 1]!;
      const to = segments[i]!;
      if (from.handleOut === undefined && to.handleIn === undefined) {
        ctx.lineTo(to.point.x, to.point.y);
      } else {
        const c1 = from.handleOut ?? from.point;
        const c2 = to.handleIn ?? to.point;
        ctx.bezierCurveTo(c1.x, c1.y, c2.x, c2.y, to.point.x, to.point.y);
      }
    }
    ctx.stroke();

    for (const anchor of anchors) {
      ctx.beginPath();
      ctx.arc(anchor.point.x, anchor.point.y, 3, 0, Math.PI * 2);
      ctx.fill();
      if (anchor.handleOut !== undefined) {
        ctx.beginPath();
        ctx.moveTo(anchor.point.x, anchor.point.y);
        ctx.lineTo(anchor.handleOut.x, anchor.handleOut.y);
        ctx.stroke();
      }
    }
    ctx.restore();
  }

  /**
   * 文档空间叠加预览：正在画的锚点/控制柄/连线；没有会话在跑但存着一条
   * 工作路径（模式=path 提交后还没转换）时，同样画出来——不然提交了却
   * 什么都看不见，用户会以为丢了。
   */
  drawOverlay(ctx: CanvasRenderingContext2D): void {
    if (this.#anchors.length > 0) {
      this.#drawAnchors(ctx, this.#anchors, false);
    } else if (this.#workPath !== null) {
      this.#drawAnchors(ctx, this.#workPath.anchors, this.#workPath.closed);
    }
  }
}
